/**
 * User Settings Module
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import type { VmSchemaAttribute } from '../api/types.js';
import { escapeHtml } from '../utils.js';
import { declareLoading, declareLoaded, confirmDialog, notificationDialog } from '../ui/dialogs.js';
import { adSyncWarning } from '../api/ad-sync.js';
import { generateButton, generateToggle, generateToggleList, getToggleListSelected, setToggleListSelected } from '../ui/forms.js';
import { renderQrCode } from '../ui/qrcode.js';
import { getSchema } from '../vm/table.js';
import { applyTheme, getStoredTheme } from '../app/theme.js';
import type { Theme } from '../app/theme.js';

function generateSettingRow(label: string, name: string, checked: boolean): string {
  return `<div class="setting-row">
    <label for="${name}">${escapeHtml(label)}</label>
    ${generateToggle(name, checked)}
  </div>`;
}

/**
 * Format a base32 secret with spaces for readability (groups of 4)
 */
function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Open the TOTP setup dialog (QR code + verification)
 */
async function openTotpSetupDialog(onSuccess: () => void): Promise<void> {
  declareLoading();
  const setup = await apiCall(api.setupTotp(), 'TOTP Setup Failed');
  declareLoaded();
  if (!setup) return;

  const content = `
    <div style="text-align: center; max-width: 360px; margin: 0 auto;">
      <p>Scan this QR code with your authenticator app:</p>
      <canvas id="totp-qr" style="margin: 12px auto; border-radius: 8px; border: 1px solid var(--color-border);"></canvas>
      <p style="font-size: 0.85em; color: #666;">
        Or enter this key manually:<br>
        <code id="totp-secret" style="user-select: all; letter-spacing: 0.1em;">${escapeHtml(formatSecret(setup.secret))}</code>
      </p>
      <hr>
      <p>Enter the 6-digit code from your app to verify:</p>
      <input type="text" id="totp-code" maxlength="6" pattern="[0-9]*"
             inputmode="numeric" autocomplete="one-time-code"
             style="font-size: 1.5em; text-align: center; width: 8em; letter-spacing: 0.3em;">
      <p id="totp-error" style="color: #d32f2f; display: none; margin-top: 8px;"></p>
    </div>
  `;

  const dlg = $('<div></div>')
    .appendTo('body')
    .html(content)
    .dialog({
      modal: true,
      title: 'Set Up Authenticator',
      zIndex: 10001,
      autoOpen: true,
      width: 420,
      resizable: false,
      buttons: {
        Verify: async function () {
          const code = ($('#totp-code').val() as string || '').trim();
          if (!/^\d{6}$/.test(code)) {
            $('#totp-error').text('Please enter a 6-digit code.').show();
            return;
          }
          $('#totp-error').hide();
          const result = await apiCall(api.confirmTotp(code), 'Verification Failed');
          if (result !== null) {
            dlg.dialog('close');
            onSuccess();
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
      open: function () {
        const canvas = document.getElementById('totp-qr') as HTMLCanvasElement;
        if (canvas) renderQrCode(canvas, setup.uri, 200);
        $('#totp-code').trigger('focus');
      },
    });
}

/**
 * Open user settings dialog
 */
export async function userSettings(): Promise<void> {
  const [userSettingsObj, userInfo] = await Promise.all([
    apiCall(api.getUserSettings(), 'Failed to Load Settings'),
    apiCall(api.getCurrentUser(), 'Failed to Load User Info'),
  ]);
  if (!userSettingsObj || !userInfo) return;

  const rdpBoolSettingsList: [string, string][] = [
    ['all_monitors', 'Use Multiple Monitors'],
    ['redirect_local_drives', 'Redirect Local Disk Drives to Remote Desktop'],
    ['download_rdp', 'Download RDP File on VM Connect'],
    ['use_domain_user', 'Pre-fill Domain User with RDP'],
    ['enable_microphone', 'Enable Microphone Passthrough'],
    ['enable_camera', 'Enable Camera Passthrough'],
    ['rdp_prompt_credentials', 'Always Prompt for Credentials on Connect'],
  ];

  const rdpBoolSelections = rdpBoolSettingsList.map((setting) => {
    const [settingName, settingDescription] = setting;
    return generateSettingRow(
      settingDescription,
      settingName,
      !!(userSettingsObj as Record<string, unknown>)[settingName]
    );
  });

  const settings = userSettingsObj as Record<string, unknown>;

  // VM table column configuration (Name, Description, Actions are always shown)
  const columnOptions: Record<string, string> = {
    cpus: 'CPUs',
    memory: 'Memory',
  };

  // Fetch schema if not already cached (e.g. navigating directly to settings)
  let schemaAttrs: VmSchemaAttribute[] = getSchema();
  if (schemaAttrs.length === 0) {
    const fetched = await apiCall(api.getVmsSchema(), 'Failed to load VM schema');
    if (fetched) schemaAttrs = fetched;
  }
  for (const attr of schemaAttrs) {
    columnOptions[attr.key] = attr.label;
  }

  const defaultCols = ['ip_address', 'rdp_users'];
  const saved = settings.vm_columns as string[] | undefined;
  const currentCols = saved?.length ? saved : defaultCols;

  const totpSection = userInfo.totp_enabled
    ? `<p>Authenticator app is <strong>enabled</strong>.</p>
       <button class="ui-button" id="btn-disable-totp">Disable Authenticator</button>`
    : `<p>Add an authenticator app for additional account security.</p>
       <button class="ui-button" id="btn-setup-totp">Set Up Authenticator</button>`;

  const currentTheme = getStoredTheme();

  const form = `
    <fieldset>
      <legend>Appearance</legend>
      <div class="setting-row">
        <label for="theme-select">Theme</label>
        <select id="theme-select" style="max-width: 200px;">
          <option value="auto"${currentTheme === 'auto' ? ' selected' : ''}>Auto (System)</option>
          <option value="light"${currentTheme === 'light' ? ' selected' : ''}>Light</option>
          <option value="dark"${currentTheme === 'dark' ? ' selected' : ''}>Dark</option>
        </select>
      </div>
    </fieldset>
    <fieldset>
      <legend>Remote Desktop Connect Options</legend>
      ${rdpBoolSelections.join('')}
    </fieldset>
    <fieldset>
      <legend>VM Table Columns</legend>
      <div class="form-grid">
        ${generateToggleList('vm_columns', Object.keys(columnOptions), currentCols, columnOptions)}
      </div>
      <button type="button" class="ui-button" id="btn-reset-columns" style="margin-top: 8px;">Reset to Default</button>
    </fieldset>
    ${generateButton('Change Login Password', 'setPassword(null)')}
    <fieldset>
      <legend>Two-Factor Authentication</legend>
      <div id="totp-section">${totpSection}</div>
    </fieldset>
  `;

  const dlg = $('<div></div>')
    .appendTo('body')
    .html('<div><h4>User Settings</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'User Settings',
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        Save: async function () {
          const selectedTheme = $('#theme-select').val() as Theme;
          applyTheme(selectedTheme);

          const newSettings: Record<string, unknown> = {};
          rdpBoolSettingsList.forEach((setting) => {
            const [settingName] = setting;
            newSettings[settingName] = $('#' + settingName).is(':checked');
          });
          newSettings.vm_columns = getToggleListSelected('vm_columns');
          const result = await apiCall(api.updateUserSettings({ settings: newSettings }), 'Failed to Save Settings');
          if (result !== null) $(this).dialog('close');
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
      open: function () {
        $('#btn-reset-columns').on('click', () => {
          setToggleListSelected('vm_columns', defaultCols);
        });

        $('#btn-setup-totp').on('click', () => {
          openTotpSetupDialog(() => {
            // Refresh settings dialog to show updated state
            dlg.dialog('close');
            userSettings();
          });
        });

        $('#btn-disable-totp').on('click', () => {
          confirmDialog(
            'Are you sure you want to disable your authenticator app? You will no longer be prompted for a code when logging in.',
            async () => {
              const result = await apiCall(api.disableTotp(), 'Failed to Disable TOTP');
              if (result !== null) {
                dlg.dialog('close');
                userSettings();
              }
            }
          );
        });
      },
    });
}

/**
 * Open password change dialog
 */
export async function setPassword(username: string | null = null): Promise<void> {
  if (!username) {
    const me = await apiCall(api.getCurrentUser(), 'Failed to Load User');
    if (!me) return;
    username = me.username;
  }

  const form = `
    <form>
      <label>Password</label><input autocomplete='new-password' id='pass1' name='pass1' type='password'><br>
      <label>Confirm Password</label><input autocomplete='new-password' id='pass2' name='pass2' type='password'><br>
    </form>
  `;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Change Password</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Change Password',
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        'Set Password': async function () {
          const pass1 = $('#pass1').val() as string;
          const pass2 = $('#pass2').val() as string;
          if (pass1 !== pass2) {
            alert('The passwords do not match');
          } else {
            declareLoading();
            const parent = $(this);
            const result = await apiCall(api.setUserPassword(username!, pass1), 'Failed to Set Password');
            declareLoaded();
            if (result !== null) {
              parent.dialog('close');
              showAdSyncWarning(result);
            }
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Tell the user a password change didn't reach every AD domain (#251).
 */
export function showAdSyncWarning(result: Parameters<typeof adSyncWarning>[0]): void {
  const warning = adSyncWarning(result);
  if (warning) notificationDialog(escapeHtml(warning).replace(/\n/g, '<br>'), true);
}

/**
 * Logout the current user
 */
export async function doLogout(): Promise<void> {
  try {
    await api.logout();
  } catch {
    // Ignore errors during logout
  }
  window.location.href = '/';
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).userSettings = userSettings;
  (window as unknown as Record<string, unknown>).setPassword = setPassword;
  (window as unknown as Record<string, unknown>).doLogout = doLogout;
}
